import React, { useEffect, useState } from 'react';
import { Button, Form, Row, Col } from 'react-bootstrap';
import { getAdminSettings, updateAdminSettings } from '../../api/context/adminSettings/index';
import Loader from '../../components/loader/loader';
import toast from 'react-hot-toast';
import ErrorState from '../../components/Error/errorState';
import { FiCheckCircle } from 'react-icons/fi';

const AdminSettings = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [settings, setSettings] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const yesNoOptions = [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' }
    ];

    const booleanSettingFields = ['isDeliveryFree', 'showVtoInApp', 'isAiLooksEnabled'];

    useEffect(() => {
        fetchAdminSettings();
    }, []);

    const fetchAdminSettings = () => {
        setLoading(true);
        setError(null);
        getAdminSettings()((response) => {
            if (response?.statusCode === 200) {
                setSettings(response.data);
            } else {
                setError(response?.message || 'Failed to fetch settings');
            }
            setLoading(false);
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox'
                ? checked
                : type === 'number'
                    ? parseFloat(value)
                    : booleanSettingFields.includes(name)
                        ? value === 'true'
                        : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSaving(true);
        updateAdminSettings(settings)((response) => {
            if (response?.statusCode === 200) {
                toast.success('Settings updated successfully');
                fetchAdminSettings();
            } else {
                toast.error(response?.message || 'Failed to update settings');
            }
            setIsSaving(false);
        });
    };

    return (
        <div className="admin-settings-page">
            <style>{`
              /* Base layout — matches Brand/Tracking pages */
              .admin-settings-page { background: #f9fafb; min-height: calc(100vh - 70px); }
              .main-content:has(.admin-settings-page) { background: #f9fafb; }
              .page-content:has(.admin-settings-page) { background: #f9fafb; }
              body:has(.admin-settings-page) .container-fluid,
              body:has(.admin-settings-page) .container {
                padding-left: 0 !important;
                padding-right: 0 !important;
              }
              body:has(.admin-settings-page) .page-content,
              body:has(.admin-settings-page) #layout-wrapper.no-topbar .page-content,
              body:has(.admin-settings-page) #layout-wrapper .page-content {
                padding-top: 0 !important;
                padding-bottom: 24px !important;
                padding-left: 16px !important;
                padding-right: 16px !important;
              }
              .admin-settings-page h3 { font-size: 18px !important; }
              .admin-settings-page .row { margin-bottom: 0; }

              /* Sticky header */
              .admin-settings-page .page-header-sticky {
                padding: clamp(14px, 1.6vw, 24px) clamp(14px, 1.4vw, 20px) clamp(10px, 1.2vw, 18px) !important;
                margin: 0 -16px 16px !important;
                top: 0 !important;
                background: #f9fafb !important;
                border-bottom: 1px solid #eef2f7 !important;
                box-shadow: none !important;
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 12px;
                flex-wrap: wrap;
              }
              @media (max-width: 991px) {
                .admin-settings-page .page-header-sticky { padding: 16px 16px 12px !important; }
              }
              @media (max-width: 767.98px) {
                .admin-settings-page .page-header-sticky {
                  padding: 10px 16px 8px !important;
                  margin: 0 -16px 8px !important;
                  position: static !important;
                }
              }

              /* Update button (desktop / tablet in header) */
              .admin-settings-page .adm-btn-primary {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
                padding: 6px 20px !important;
                border-radius: 6px !important;
                font-size: 12.5px !important;
                font-weight: 600 !important;
                height: 36px;
                background: #008695 !important;
                border: 1px solid #008695 !important;
                color: #fff !important;
                cursor: pointer;
                box-shadow: 0 2px 6px rgba(0,134,149,0.2);
                transition: all 0.15s ease;
                white-space: nowrap;
              }
              .admin-settings-page .adm-btn-primary:hover:not(:disabled) {
                background: #006672 !important;
                border-color: #006672 !important;
              }
              .admin-settings-page .adm-btn-primary:disabled {
                opacity: 0.7;
                cursor: not-allowed;
              }
              @media (max-width: 575.98px) {
                /* Hide the header button on mobile — a fixed bottom bar takes over */
                .admin-settings-page .adm-header-btn { display: none !important; }
              }

              /* Settings card */
              .admin-settings-page .adm-card {
                background: #fff;
                border: 1px solid #e2e8f0;
                border-radius: 12px;
                padding: 20px;
                box-shadow: 0 1px 2px rgba(15,23,42,0.03);
              }
              @media (max-width: 767.98px) {
                .admin-settings-page .adm-card { padding: 14px; border-radius: 10px; }
              }

              /* Field label + control */
              .admin-settings-page .adm-field { margin-bottom: 16px; }
              .admin-settings-page .adm-field .form-label {
                font-size: 11px !important;
                font-weight: 700 !important;
                color: #475569 !important;
                text-transform: uppercase;
                letter-spacing: 0.03em;
                margin-bottom: 6px !important;
                display: block;
              }
              .admin-settings-page .adm-input,
              .admin-settings-page .adm-select {
                border: 1px solid #e2e8f0 !important;
                border-radius: 6px !important;
                background: #fff !important;
                color: #0f172a !important;
                font-size: 12.5px !important;
                height: 36px !important;
                padding: 6px 12px !important;
                transition: border-color 0.15s ease, box-shadow 0.15s ease;
              }
              .admin-settings-page .adm-input:focus,
              .admin-settings-page .adm-select:focus {
                border-color: #008695 !important;
                box-shadow: 0 0 0 3px rgba(0,134,149,0.15) !important;
                outline: none;
              }

              /* Mobile fixed bottom update bar */
              .admin-settings-page .adm-mobile-actionbar { display: none; }
              @media (max-width: 575.98px) {
                .admin-settings-page {
                  padding-bottom: calc(60px + env(safe-area-inset-bottom, 0px));
                }
                .admin-settings-page .adm-mobile-actionbar {
                  display: flex;
                  position: fixed;
                  left: 0; right: 0; bottom: 0;
                  padding: 8px 12px calc(8px + env(safe-area-inset-bottom, 0px));
                  background: #fff;
                  border-top: 1px solid #e2e8f0;
                  box-shadow: 0 -2px 8px rgba(15,23,42,0.05);
                  z-index: 1049;
                  gap: 8px;
                }
                .admin-settings-page .adm-mobile-actionbar .adm-btn-primary {
                  flex: 1;
                  height: 42px;
                  font-size: 13px !important;
                }
              }
            `}</style>

            <div className="page-header-sticky">
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.01em' }}>Admin Settings</h3>

                {!loading && settings !== null && (
                    <button
                        type="submit"
                        form="admin-settings-form"
                        className="adm-btn-primary adm-header-btn"
                        disabled={isSaving}
                    >
                        {isSaving ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                Updating...
                            </>
                        ) : (
                            <>
                                <FiCheckCircle size={15} strokeWidth={2} />
                                Update Settings
                            </>
                        )}
                    </button>
                )}
            </div>

            <div className="adm-card">
                {loading ? (
                    <div className="text-center py-4"><Loader /></div>
                ) : settings === null ? (
                    <ErrorState onRetry={fetchAdminSettings} />
                ) : (
                    <Form id="admin-settings-form" onSubmit={handleSubmit}>
                        <Row className="gx-2 gx-md-3">
                            <Col xs={6} md={6} lg={4}>
                                <Form.Group className="adm-field" controlId="locationRadius">
                                    <Form.Label>Location Radius (km)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="locationRadius"
                                        value={settings.locationRadius || 0}
                                        onChange={handleInputChange}
                                        className="adm-input"
                                        min="0"
                                        step="1"
                                    />
                                </Form.Group>
                            </Col>

                            <Col xs={6} md={6} lg={4}>
                                <Form.Group className="adm-field" controlId="handlingCharge">
                                    <Form.Label>Handling Charge (₹)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="handlingCharge"
                                        value={settings.handlingCharge || 0}
                                        onChange={handleInputChange}
                                        className="adm-input"
                                        min="0"
                                        step="0.01"
                                    />
                                </Form.Group>
                            </Col>

                            <Col xs={6} md={6} lg={4}>
                                <Form.Group className="adm-field" controlId="limitForVtoUse">
                                    <Form.Label>Virtual Try-On Limit</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="limitForVtoUse"
                                        value={settings.limitForVtoUse || 0}
                                        onChange={handleInputChange}
                                        className="adm-input"
                                        min="0"
                                        step="1"
                                    />
                                </Form.Group>
                            </Col>

                            <Col xs={6} md={6} lg={4}>
                                <Form.Group className="adm-field" controlId="numberOfDatesForNew">
                                    <Form.Label>New Arrival Tag (days)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="numberOfDatesForNew"
                                        value={settings.numberOfDatesForNew || 0}
                                        onChange={handleInputChange}
                                        className="adm-input"
                                        min="0"
                                        step="1"
                                    />
                                </Form.Group>
                            </Col>

                            <Col xs={6} md={6} lg={4}>
                                <Form.Group className="adm-field" controlId="isDeliveryFree">
                                    <Form.Label>Free Delivery</Form.Label>
                                    <Form.Select
                                        name="isDeliveryFree"
                                        value={String(settings.isDeliveryFree)}
                                        onChange={handleInputChange}
                                        className="adm-select"
                                    >
                                        {yesNoOptions.map(option => (
                                            <option key={String(option.value)} value={String(option.value)}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col xs={6} md={6} lg={4}>
                                <Form.Group className="adm-field" controlId="showVtoInApp">
                                    <Form.Label>Virtual Try-On</Form.Label>
                                    <Form.Select
                                        name="showVtoInApp"
                                        value={String(settings.showVtoInApp)}
                                        onChange={handleInputChange}
                                        className="adm-select"
                                    >
                                        {yesNoOptions.map(option => (
                                            <option key={String(option.value)} value={String(option.value)}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col xs={6} md={6} lg={4}>
                                <Form.Group className="adm-field" controlId="isAiLooksEnabled">
                                    <Form.Label>AI Looks</Form.Label>
                                    <Form.Select
                                        name="isAiLooksEnabled"
                                        value={String(settings.isAiLooksEnabled)}
                                        onChange={handleInputChange}
                                        className="adm-select"
                                    >
                                        {yesNoOptions.map(option => (
                                            <option key={String(option.value)} value={String(option.value)}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                )}
            </div>

            {/* Mobile-only fixed bottom action bar */}
            {!loading && settings !== null && (
                <div className="adm-mobile-actionbar">
                    <button
                        type="submit"
                        form="admin-settings-form"
                        className="adm-btn-primary"
                        disabled={isSaving}
                    >
                        {isSaving ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                Updating...
                            </>
                        ) : (
                            <>
                                <FiCheckCircle size={15} strokeWidth={2} />
                                Update Settings
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default AdminSettings;
